import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { Paginated } from "@/lib/types";

/**
 * /api/v1/news/ — CEO + Admin author official news items. Read is open
 * to any signed-in user; writes 403 for everyone below CEO.
 */
export type NewsStatus = "draft" | "published";

export interface NewsRow {
  id: string;
  title: string;
  slug: string;
  image_url: string | null;
  status: NewsStatus;
  status_display: string;
  view_count: number;
  external_source_name: string;
  external_source_url: string;
  created_at: string;
  updated_at: string;
}

export interface NewsDetail extends NewsRow {
  content: string;
}

const KEY = ["news"];

// ---------- READ ----------
export function useNews(opts: { search?: string; page?: number; status?: NewsStatus | null } = {}) {
  return useQuery<Paginated<NewsRow>>({
    queryKey: [...KEY, "list", opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.search)  params.set("search", opts.search);
      if (opts.page)    params.set("page", String(opts.page));
      if (opts.status)  params.set("status", opts.status);
      const { data } = await api.get<Paginated<NewsRow>>(
        `/news/${params.toString() ? "?" + params.toString() : ""}`,
      );
      return data;
    },
    staleTime: 30_000,
  });
}

export function useNewsItem(id: string | undefined) {
  return useQuery<NewsDetail>({
    enabled: !!id,
    queryKey: [...KEY, "detail", id],
    queryFn: async () => {
      const { data } = await api.get<NewsDetail>(`/news/${id}/`);
      return data;
    },
  });
}

// ---------- WRITE (CEO + Admin only — server enforces) ----------
export interface CreateNewsInput {
  title: string;
  content: string;
  status: NewsStatus;
  image?: File;
}

export function useCreateNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNewsInput) => {
      const form = new FormData();
      form.append("title", input.title);
      form.append("content", input.content);
      form.append("status", input.status);
      if (input.image) form.append("image", input.image);
      const { data } = await api.post<NewsDetail>("/news/", form);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface UpdateNewsInput {
  id: string;
  title?: string;
  content?: string;
  status?: NewsStatus;
  image?: File;
}

export function useUpdateNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: UpdateNewsInput) => {
      const form = new FormData();
      if (rest.title   != null) form.append("title", rest.title);
      if (rest.content != null) form.append("content", rest.content);
      if (rest.status  != null) form.append("status", rest.status);
      if (rest.image   != null) form.append("image", rest.image);
      const { data } = await api.patch<NewsDetail>(`/news/${id}/`, form);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/news/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ---------- BULK ----------
export function useBulkDeleteNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`/news/${id}/`)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      return { ok, fail: results.length - ok };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
